import type { ChatTransportSendArgs } from "./types";
import { ChatStreamError } from "@/components/analysis/chat/v2/utils/streamChatMessage";
import { sleep } from "@/lib/utils";

const STREAM_STALL_MS = 15_000;
const STALL_CHECK_INTERVAL_MS = 1_000;
// 10 attempts in total, up to ~198s if every attempt stalls.
const RETRY_BACKOFF_MS = [
  0, 0, 1_000, 2_000, 5_000, 10_000, 10_000, 10_000, 10_000,
];

export type StreamAttemptArgs = Pick<
  ChatTransportSendArgs,
  | "replayFrom"
  | "replayOnly"
  | "isNewChat"
  | "signal"
  | "onEvent"
  | "onStarted"
  | "onStreamActivity"
>;

export type StreamRunResult =
  | { type: "completed" }
  | { type: "aborted" }
  | { type: "failed"; error: unknown };

/**
 * Drives one chat turn to a terminal event. After the server accepts the
 * message, every reconnect replays from the last seen event instead of resending.
 */
export const runChatStream = async ({
  openStream,
  isNewChat,
  replayOnly,
  signal,
  onEvent,
  onRetry,
  onRetriesExhausted,
  stallMs = STREAM_STALL_MS,
}: {
  openStream: (args: StreamAttemptArgs) => Promise<void>;
  isNewChat: boolean;
  replayOnly: boolean;
  signal: AbortSignal;
  onEvent: StreamAttemptArgs["onEvent"];
  onRetry: (args: { attempt: number; reason: "stall" | "error" }) => void;
  onRetriesExhausted: () => void;
  stallMs?: number;
}): Promise<StreamRunResult> => {
  const progress = {
    lastEventIndex: -1,
    hasServerAccepted: false,
    lastActivityAt: Date.now(),
  };
  const touch = () => {
    progress.lastActivityAt = Date.now();
  };

  const openOnce = async (): Promise<
    StreamRunResult | { type: "retryable"; reason: "stall" | "error" }
  > => {
    const attemptController = new AbortController();
    const abortAttempt = () => attemptController.abort();
    signal.addEventListener("abort", abortAttempt);
    const outcome = { didStall: false, sawTerminalEvent: false };
    touch();
    const stallTimer = setInterval(() => {
      if (Date.now() - progress.lastActivityAt >= stallMs) {
        outcome.didStall = true;
        attemptController.abort();
      }
    }, STALL_CHECK_INTERVAL_MS);

    try {
      await openStream({
        replayFrom: progress.lastEventIndex + 1,
        replayOnly: replayOnly || progress.hasServerAccepted,
        isNewChat: isNewChat && !progress.hasServerAccepted,
        signal: attemptController.signal,
        onStarted: () => {
          progress.hasServerAccepted = true;
          touch();
        },
        onStreamActivity: touch,
        onEvent: (envelope) => {
          if (attemptController.signal.aborted) {
            return;
          }
          touch();
          progress.lastEventIndex = Math.max(
            progress.lastEventIndex,
            envelope.eventIndex
          );
          const isTerminal =
            envelope.event.type === "finish" || envelope.event.type === "error";
          if (isTerminal) {
            outcome.sawTerminalEvent = true;
          }
          onEvent(envelope);
        },
      });
      if (outcome.sawTerminalEvent) {
        return { type: "completed" };
      }
      throw new Error("Chat stream ended without a terminal event");
    } catch (error) {
      if (signal.aborted) {
        return { type: "aborted" };
      }
      if (outcome.sawTerminalEvent) {
        return { type: "completed" };
      }
      if (outcome.didStall) {
        return { type: "retryable", reason: "stall" };
      }
      return error instanceof ChatStreamError
        ? { type: "failed", error }
        : { type: "retryable", reason: "error" };
    } finally {
      clearInterval(stallTimer);
      signal.removeEventListener("abort", abortAttempt);
    }
  };

  const runAttempt = async (attempt: number): Promise<StreamRunResult> => {
    if (signal.aborted) {
      return { type: "aborted" };
    }
    const result = await openOnce();
    if (result.type !== "retryable") {
      return result;
    }
    if (attempt >= RETRY_BACKOFF_MS.length) {
      onRetriesExhausted();
      return {
        type: "failed",
        error: new Error(`Chat stream gave up after ${attempt + 1} attempts`),
      };
    }
    onRetry({ attempt, reason: result.reason });
    await sleep(RETRY_BACKOFF_MS[attempt]);
    return runAttempt(attempt + 1);
  };

  return runAttempt(0);
};
